import { useRef, useState, useEffect, useCallback } from "react";

// ── Palette ───────────────────────────────────────────────────────────────────
const GOLD        = "#C9A96E";
const INK         = "#1C1915";
const WHITE       = "#FFFFFF";
const POLY_COLOR  = "#27AE60";
const LINE_COLOR  = "#5DADE2";
const CALIB_COLOR = "#E74C3C";
const CANVAS_W    = 1100;
const CANVAS_H    = 640;

// ── Maths ─────────────────────────────────────────────────────────────────────
function dist(a, b) { return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2); }
function shoelace(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(a) / 2;
}

// ── DXF Mini-parser (LINE + LWPOLYLINE) ───────────────────────────────────────
function parseDxfSegs(text) {
  const rows = text.split(/\r?\n/);
  const segs = [];
  let inEnt = false, eType = null, eV = {}, lwPts = [], lwX = null;

  const flush = () => {
    if (eType === "LINE") {
      const x1 = +eV[10], y1 = +eV[20], x2 = +eV[11], y2 = +eV[21];
      if (isFinite(x1) && isFinite(y1) && isFinite(x2) && isFinite(y2))
        segs.push([x1, y1, x2, y2]);
    } else if ((eType === "LWPOLYLINE" || eType === "POLYLINE") && lwPts.length >= 2) {
      for (let j = 0; j < lwPts.length - 1; j++)
        segs.push([lwPts[j][0], lwPts[j][1], lwPts[j + 1][0], lwPts[j + 1][1]]);
    }
    eV = {}; lwPts = []; lwX = null;
  };

  for (let i = 0; i + 1 < rows.length; i += 2) {
    const code = parseInt(rows[i].trim(), 10);
    const val  = rows[i + 1]?.trim() ?? "";
    if (isNaN(code)) continue;
    if (code === 0) {
      flush();
      if (val === "ENTITIES") { inEnt = true; eType = null; continue; }
      if (val === "ENDSEC" && inEnt) break;
      if (inEnt) eType = val;
      continue;
    }
    if (!inEnt || !eType) continue;
    if (eType === "LINE") eV[code] = val;
    else if (eType === "LWPOLYLINE" || eType === "POLYLINE") {
      if (code === 10) lwX = parseFloat(val);
      else if (code === 20 && lwX !== null) { lwPts.push([lwX, parseFloat(val)]); lwX = null; }
    }
  }
  flush();
  return segs;
}

function dxfFit(segs) {
  if (!segs.length) return null;
  let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
  segs.forEach(([x1, y1, x2, y2]) => {
    mnX = Math.min(mnX, x1, x2); mnY = Math.min(mnY, y1, y2);
    mxX = Math.max(mxX, x1, x2); mxY = Math.max(mxY, y1, y2);
  });
  const pad = 40;
  const s = Math.min(
    (CANVAS_W - 2 * pad) / (mxX - mnX || 1),
    (CANVAS_H - 2 * pad) / (mxY - mnY || 1)
  );
  return { s, ox: pad - mnX * s, oy: CANVAS_H - pad + mnY * s }; // Y flipped
}

// ── PDF → Image (via PDF.js CDN) ──────────────────────────────────────────────
async function loadPdfAsImage(file) {
  if (!window.pdfjsLib) {
    await new Promise((res, rej) => {
      const sc = document.createElement("script");
      sc.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      sc.onload = res; sc.onerror = rej;
      document.head.appendChild(sc);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  }
  const buf  = await file.arrayBuffer();
  const pdf  = await window.pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const vp   = page.getViewport({ scale: 2 });
  const off  = document.createElement("canvas");
  off.width  = vp.width; off.height = vp.height;
  await page.render({ canvasContext: off.getContext("2d"), viewport: vp }).promise;
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = off.toDataURL("image/jpeg", 0.92);
  });
}

// ── Level factory ─────────────────────────────────────────────────────────────
let _lvlId = 0;
function newLevel(name = "RDC", hsp = 2.8) {
  return {
    id: ++_lvlId, name, hsp,
    planType: "none",          // "none" | "image" | "pdf" | "dxf"
    imgObj: null, imgRect: null,
    dxfSegs: null, dxfFitData: null,
    scale: null, calPts: [], done: [],
    manualSurface: "", manualPerimetre: "",
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function CanvasMeasure({ onMeasure, onClose, onAutoCalc }) {
  const canvasRef = useRef(null);

  // ── Multi-level state ──
  const [levels,       setLevels]       = useState([newLevel("RDC", 2.8)]);
  const [activeLvlIdx, setActiveLvlIdx] = useState(0);

  // ── Building params ──
  const [buildingParams, setBuildingParams] = useState({
    typeConstruction: "résidentiel",
    zone: "",
    notes: "",
  });
  const [showParams, setShowParams] = useState(false);

  // ── Transient drawing state (resets on level switch) ──
  const [mode,       setMode]       = useState("idle");
  const [drawPts,    setDrawPts]    = useState([]);
  const [calDist,    setCalDist]    = useState("");
  const [calErr,     setCalErr]     = useState("");
  const [mouse,      setMouse]      = useState(null);
  const [pending,    setPending]    = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Derived from active level
  const activeLvl  = levels[activeLvlIdx] || levels[0];
  const imgObj     = activeLvl?.imgObj      || null;
  const dxfSegs    = activeLvl?.dxfSegs    || null;
  const dxfFitData = activeLvl?.dxfFitData || null;
  const scale      = activeLvl?.scale      || null;
  const calPts     = activeLvl?.calPts     || [];
  const done       = activeLvl?.done       || [];
  const planType   = activeLvl?.planType   || "none";

  // ── Level helpers ──────────────────────────────────────────────────────────
  const updateLevel = useCallback((idx, updates) => {
    setLevels(prev => prev.map((l, i) => i === idx ? { ...l, ...updates } : l));
  }, []);

  const switchLevel = useCallback((idx) => {
    setActiveLvlIdx(idx);
    setMode("idle"); setDrawPts([]); setCalDist(""); setCalErr("");
    setPending(null); setMouse(null);
  }, []);

  const addLevel = () => {
    const presets = ["RDC", "R+1", "R+2", "R+3", "Sous-sol", "Terrasse", "Mezzanine"];
    const name = presets[levels.length] || `Niveau ${levels.length}`;
    const nl = newLevel(name, 2.8);
    setLevels(prev => [...prev, nl]);
    // switch after state settles
    setTimeout(() => switchLevel(levels.length), 0);
  };

  const removeLevel = (idx) => {
    if (levels.length <= 1) return;
    setLevels(prev => prev.filter((_, i) => i !== idx));
    setActiveLvlIdx(i => Math.min(i, levels.length - 2));
    setMode("idle"); setDrawPts([]);
  };

  // ── Canvas coordinate helper ───────────────────────────────────────────────
  function evToCanvas(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (CANVAS_W / rect.width),
      y: (e.clientY - rect.top)  * (CANVAS_H / rect.height),
    };
  }

  // ── File loading ───────────────────────────────────────────────────────────
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fname = file.name.toLowerCase();
    setCalErr("");

    if (/\.dwg$/i.test(fname)) {
      setCalErr("DWG non supporté. Exportez en PDF ou DXF depuis AutoCAD / ArchiCAD.");
      e.target.value = ""; return;
    }

    if (/\.dxf$/i.test(fname)) {
      const text = await file.text();
      const segs = parseDxfSegs(text);
      if (!segs.length) { setCalErr("Aucune géométrie trouvée dans ce fichier DXF."); e.target.value = ""; return; }
      const fit = dxfFit(segs);
      updateLevel(activeLvlIdx, {
        planType: "dxf", imgObj: null, imgRect: null,
        dxfSegs: segs, dxfFitData: fit,
        scale: null, calPts: [], done: [],
      });
      setMode("idle"); setDrawPts([]);

    } else if (file.type === "application/pdf" || /\.pdf$/i.test(fname)) {
      setPdfLoading(true);
      try {
        const img = await loadPdfAsImage(file);
        updateLevel(activeLvlIdx, {
          planType: "pdf", imgObj: img, imgRect: fitImage(img),
          dxfSegs: null, dxfFitData: null,
          scale: null, calPts: [], done: [],
        });
        setMode("idle"); setDrawPts([]);
      } catch (err) {
        setCalErr("Erreur PDF : " + (err.message || String(err)));
      } finally {
        setPdfLoading(false);
      }

    } else if (file.type.startsWith("image/") || /\.(png|jpe?g|webp|bmp|tiff?)$/i.test(fname)) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        updateLevel(activeLvlIdx, {
          planType: "image", imgObj: img, imgRect: fitImage(img),
          dxfSegs: null, dxfFitData: null,
          scale: null, calPts: [], done: [],
        });
        setMode("idle"); setDrawPts([]);
      };
      img.src = url;
    } else {
      setCalErr("Format non reconnu. Acceptés : PNG, JPG, PDF, DXF.");
    }
    e.target.value = "";
  }

  function fitImage(img) {
    const pad = 20;
    const aspect = img.width / img.height;
    let iW = CANVAS_W - pad * 2, iH = iW / aspect;
    if (iH > CANVAS_H - pad * 2) { iH = CANVAS_H - pad * 2; iW = iH * aspect; }
    return { x: (CANVAS_W - iW) / 2, y: (CANVAS_H - iH) / 2, w: iW, h: iH };
  }

  // ── Draw ───────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    ctx.fillStyle = "#0D0B09";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // ── Plan background ──
    if ((planType === "image" || planType === "pdf") && imgObj && activeLvl.imgRect) {
      const { x, y, w, h } = activeLvl.imgRect;
      ctx.drawImage(imgObj, x, y, w, h);
      ctx.strokeStyle = "rgba(201,169,110,0.3)"; ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);

    } else if (planType === "dxf" && dxfSegs && dxfFitData) {
      ctx.fillStyle = "#F8F6F1";
      ctx.fillRect(10, 10, CANVAS_W - 20, CANVAS_H - 20);
      const { s, ox, oy } = dxfFitData;
      ctx.strokeStyle = "#2C3E50"; ctx.lineWidth = 0.9; ctx.beginPath();
      dxfSegs.forEach(([x1, y1, x2, y2]) => {
        ctx.moveTo(ox + x1 * s, oy - y1 * s);
        ctx.lineTo(ox + x2 * s, oy - y2 * s);
      });
      ctx.stroke();
      // DXF label
      ctx.fillStyle = "rgba(44,62,80,0.5)"; ctx.font = "11px DM Sans,sans-serif";
      ctx.fillText(`DXF — ${dxfSegs.length} entités`, 16, CANVAS_H - 14);

    } else {
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      ctx.fillRect(20, 20, CANVAS_W - 40, CANVAS_H - 40);
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(255,255,255,0.16)"; ctx.font = "bold 16px DM Sans,sans-serif";
      ctx.fillText("📂 Chargez un plan pour ce niveau", CANVAS_W / 2, CANVAS_H / 2 - 16);
      ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.font = "12px DM Sans,sans-serif";
      ctx.fillText("Formats acceptés : PNG · JPG · PDF · DXF", CANVAS_W / 2, CANVAS_H / 2 + 8);
      ctx.fillText("Pour les fichiers DWG : exportez en DXF ou PDF depuis AutoCAD / ArchiCAD", CANVAS_W / 2, CANVAS_H / 2 + 26);
      ctx.textAlign = "left";
    }

    // ── Completed measurements ──
    done.forEach(m => {
      if (m.type === "polygon" && m.pts.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(m.pts[0].x, m.pts[0].y);
        m.pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.strokeStyle = POLY_COLOR; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = "rgba(39,174,96,0.18)"; ctx.fill();
        const cx = m.pts.reduce((s, p) => s + p.x, 0) / m.pts.length;
        const cy = m.pts.reduce((s, p) => s + p.y, 0) / m.pts.length;
        const lbl = `${m.value.toFixed(2)} m²`;
        ctx.font = "bold 13px DM Sans,sans-serif";
        const tw = ctx.measureText(lbl).width;
        ctx.fillStyle = POLY_COLOR; ctx.fillRect(cx - tw / 2 - 6, cy - 18, tw + 12, 22);
        ctx.fillStyle = WHITE; ctx.textAlign = "center";
        ctx.fillText(lbl, cx, cy - 1); ctx.textAlign = "left";

      } else if (m.type === "line" && m.pts.length === 2) {
        ctx.beginPath();
        ctx.moveTo(m.pts[0].x, m.pts[0].y); ctx.lineTo(m.pts[1].x, m.pts[1].y);
        ctx.strokeStyle = LINE_COLOR; ctx.lineWidth = 2.5; ctx.stroke();
        const mx = (m.pts[0].x + m.pts[1].x) / 2;
        const my = (m.pts[0].y + m.pts[1].y) / 2;
        const lbl = `${m.value.toFixed(2)} ml`;
        ctx.font = "bold 13px DM Sans,sans-serif";
        const tw = ctx.measureText(lbl).width;
        ctx.fillStyle = LINE_COLOR; ctx.fillRect(mx - tw / 2 - 6, my - 22, tw + 12, 22);
        ctx.fillStyle = WHITE; ctx.textAlign = "center";
        ctx.fillText(lbl, mx, my - 5); ctx.textAlign = "left";
      }
    });

    // ── Calibration points ──
    if (calPts.length > 0) {
      dotC(ctx, calPts[0], CALIB_COLOR, 7);
      if (calPts.length > 1) {
        dotC(ctx, calPts[1], CALIB_COLOR, 7);
        lineC(ctx, calPts[0], calPts[1], CALIB_COLOR, 2, []);
      } else if (mouse && mode === "calibrate") {
        lineC(ctx, calPts[0], mouse, CALIB_COLOR, 1.5, [4, 4]);
      }
    }

    // ── In-progress drawing ──
    if (drawPts.length > 0) {
      const color = mode === "polygon" ? POLY_COLOR : LINE_COLOR;
      if (mode === "polygon") {
        ctx.beginPath();
        ctx.moveTo(drawPts[0].x, drawPts[0].y);
        drawPts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        if (mouse) ctx.lineTo(mouse.x, mouse.y);
        ctx.closePath();
        ctx.strokeStyle = color; ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]);
        ctx.fillStyle = "rgba(39,174,96,0.10)"; ctx.fill();
        drawPts.forEach(p => dotC(ctx, p, color, 5));
        if (mouse && scale) {
          const preview = shoelace([...drawPts, mouse]) / (scale * scale);
          ctx.fillStyle = "rgba(0,0,0,0.75)"; ctx.font = "bold 12px sans-serif";
          ctx.fillText(`≈ ${preview.toFixed(2)} m²`, mouse.x + 12, mouse.y - 8);
        }
      } else if (mode === "line") {
        dotC(ctx, drawPts[0], color, 5);
        if (mouse) {
          lineC(ctx, drawPts[0], mouse, color, 2, [5, 5]);
          if (scale) {
            const d = dist(drawPts[0], mouse) / scale;
            ctx.fillStyle = "rgba(0,0,0,0.75)"; ctx.font = "bold 12px sans-serif";
            ctx.fillText(`≈ ${d.toFixed(2)} ml`, mouse.x + 12, mouse.y - 8);
          }
        }
      }
    }

    // ── Scale indicator ──
    if (scale) {
      const barM = 5, barPx = barM * scale;
      if (barPx < CANVAS_W - 40) {
        const bx = CANVAS_W - barPx - 20, by = CANVAS_H - 20;
        ctx.fillStyle = "rgba(0,0,0,0.65)"; ctx.fillRect(bx - 8, by - 20, barPx + 16, 24);
        ctx.fillStyle = WHITE; ctx.fillRect(bx, by - 14, barPx, 6);
        ctx.fillStyle = GOLD; ctx.font = "11px DM Sans,sans-serif"; ctx.textAlign = "center";
        ctx.fillText(`${barM} m`, bx + barPx / 2, by + 6); ctx.textAlign = "left";
      }
    }

    // ── PDF loading overlay ──
    if (pdfLoading) {
      ctx.fillStyle = "rgba(0,0,0,0.72)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = GOLD; ctx.font = "bold 18px DM Sans,sans-serif"; ctx.textAlign = "center";
      ctx.fillText("⏳ Chargement du PDF…", CANVAS_W / 2, CANVAS_H / 2); ctx.textAlign = "left";
    }
  }, [activeLvl, imgObj, dxfSegs, dxfFitData, scale, calPts, done, drawPts, mouse, mode, planType, pdfLoading]);

  function dotC(ctx, p, color, r) {
    ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
    ctx.strokeStyle = WHITE; ctx.lineWidth = 1.5; ctx.stroke();
  }
  function lineC(ctx, a, b, color, w, dash = []) {
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    ctx.strokeStyle = color; ctx.lineWidth = w;
    ctx.setLineDash(dash); ctx.stroke(); ctx.setLineDash([]);
  }

  useEffect(() => { draw(); }, [draw]);

  // ── Event handlers ─────────────────────────────────────────────────────────
  const handleMouseMove = e => setMouse(evToCanvas(e));

  const handleClick = e => {
    const pt = evToCanvas(e);

    if (mode === "calibrate") {
      const next = [...calPts, pt];
      updateLevel(activeLvlIdx, { calPts: next });
      return;
    }
    if (mode === "polygon") { setDrawPts(p => [...p, pt]); return; }
    if (mode === "line") {
      if (drawPts.length === 0) { setDrawPts([pt]); return; }
      if (!scale) { setCalErr("Calibrez d'abord l'échelle !"); return; }
      const d = dist(drawPts[0], pt) / scale;
      const m = { id: Date.now(), type: "line", pts: [drawPts[0], pt], value: d };
      updateLevel(activeLvlIdx, { done: [...done, m] });
      setPending({ value: +d.toFixed(3), unite: "ml", label: `${d.toFixed(2)} ml` });
      setDrawPts([]); setMode("idle");
    }
  };

  const handleDblClick = e => {
    if (mode === "polygon" && drawPts.length >= 3 && scale) {
      const a = shoelace(drawPts) / (scale * scale);
      const m = { id: Date.now(), type: "polygon", pts: [...drawPts], value: a };
      updateLevel(activeLvlIdx, { done: [...done, m] });
      setPending({ value: +a.toFixed(3), unite: "m²", label: `${a.toFixed(2)} m²` });
      setDrawPts([]); setMode("idle");
    }
  };

  const confirmCalibration = () => {
    const d = parseFloat(calDist);
    if (!d || d <= 0) { setCalErr("Entrez une distance valide (en mètres)"); return; }
    const px = dist(calPts[0], calPts[1]);
    updateLevel(activeLvlIdx, { scale: px / d, calPts: [] });
    setCalDist(""); setCalErr(""); setMode("idle");
  };

  const startMode = m => {
    if (m !== "calibrate" && !scale) { setCalErr("Calibrez d'abord l'échelle !"); return; }
    setDrawPts([]); setMode(m); setCalErr("");
    if (m === "calibrate") updateLevel(activeLvlIdx, { calPts: [] });
  };

  const undo = () => {
    if (drawPts.length > 0) { setDrawPts(p => p.slice(0, -1)); return; }
    if (done.length > 0) updateLevel(activeLvlIdx, { done: done.slice(0, -1) });
  };

  // ── Level stats ────────────────────────────────────────────────────────────
  const lvlSurface   = done.filter(m => m.type === "polygon").reduce((s, m) => s + m.value, 0);
  const lvlPerimetre = done.filter(m => m.type === "line").reduce((s, m) => s + m.value, 0);

  const totalSurface = levels.reduce((s, lvl) => {
    const poly = lvl.done.filter(m => m.type === "polygon").reduce((a, m) => a + m.value, 0);
    return s + (poly > 0 ? poly : parseFloat(lvl.manualSurface) || 0);
  }, 0);

  // ── Auto-calc ──────────────────────────────────────────────────────────────
  const handleAutoCalc = () => {
    if (onAutoCalc) onAutoCalc({ levels, buildingParams });
    onClose();
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const btn = (active, extra = {}) => ({
    padding: "7px 12px", borderRadius: 7, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
    background: active ? GOLD : "rgba(255,255,255,0.08)",
    color: active ? INK : WHITE, transition: "all .15s", ...extra,
  });
  const inp = {
    background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)",
    color: WHITE, borderRadius: 6, padding: "5px 9px", fontSize: 12,
    fontFamily: "'DM Sans',sans-serif", width: "100%", boxSizing: "border-box",
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 2000, display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif" }}>

      {/* ── TOOLBAR ── */}
      <div style={{ display: "flex", gap: 6, padding: "8px 14px", background: INK, borderBottom: `2px solid ${GOLD}`, alignItems: "center", flexWrap: "wrap", minHeight: 52 }}>
        <div style={{ color: GOLD, fontWeight: 700, fontSize: 14, marginRight: 6, whiteSpace: "nowrap" }}>📐 Métré Intelligent</div>

        {/* ── Level tabs ── */}
        <div style={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
          {levels.map((lvl, idx) => (
            <div key={lvl.id} style={{ display: "flex", alignItems: "center" }}>
              <button onClick={() => switchLevel(idx)}
                style={{ ...btn(idx === activeLvlIdx), borderRadius: levels.length > 1 ? "6px 0 0 6px" : 6 }}>
                {lvl.name}
                {lvl.done.filter(m => m.type === "polygon").length > 0 && (
                  <span style={{ marginLeft: 5, background: POLY_COLOR, color: WHITE, borderRadius: 10, padding: "1px 5px", fontSize: 10 }}>
                    {lvl.done.filter(m => m.type === "polygon").reduce((s, m) => s + m.value, 0).toFixed(0)} m²
                  </span>
                )}
              </button>
              {levels.length > 1 && (
                <button onClick={() => removeLevel(idx)}
                  style={{ ...btn(idx === activeLvlIdx), borderRadius: "0 6px 6px 0", padding: "7px 7px", color: "#FF9999", borderLeft: "1px solid rgba(0,0,0,0.25)" }}>
                  ✕
                </button>
              )}
            </div>
          ))}
          <button onClick={addLevel} title="Ajouter un niveau" style={{ ...btn(false), padding: "7px 11px", color: GOLD, fontSize: 16 }}>+</button>
        </div>

        <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.1)" }} />

        {/* File input */}
        <label style={{ ...btn(false, { cursor: "pointer" }) }}>
          {pdfLoading ? "⏳ Chargement…" : "🗂 Charger plan"}
          <input type="file" accept="image/*,application/pdf,.pdf,.dxf,.dwg"
            onChange={handleFile} style={{ display: "none" }} disabled={pdfLoading} />
        </label>

        <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.1)" }} />

        {/* Calibrate */}
        <button onClick={() => startMode("calibrate")} style={btn(mode === "calibrate")}>
          📏 {scale ? `Recal. (1m≈${Math.round(scale)}px)` : "① Étalonner"}
        </button>

        <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.1)" }} />

        {/* Measure modes */}
        <button onClick={() => startMode("polygon")} style={btn(mode === "polygon")}
          disabled={planType === "none"} title="Tracer une surface (m²)">⬡ Surface</button>
        <button onClick={() => startMode("line")} style={btn(mode === "line")}
          disabled={planType === "none"} title="Mesurer une distance (ml)">↔ Linéaire</button>
        <button onClick={undo} style={btn(false)} disabled={done.length === 0 && drawPts.length === 0}>↩</button>
        <button onClick={() => updateLevel(activeLvlIdx, { done: [] })}
          style={btn(false)} disabled={done.length === 0} title="Effacer mesures de ce niveau">🗑</button>

        <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.1)" }} />

        <button onClick={() => setShowParams(p => !p)} style={{ ...btn(showParams), whiteSpace: "nowrap" }}>
          ⚙ Paramètres bâtiment
        </button>

        <div style={{ flex: 1 }} />
        {calErr && <span style={{ color: "#FF7070", fontSize: 12, maxWidth: 240 }}>{calErr}</span>}

        {/* Actions */}
        {onAutoCalc && (
          <button onClick={handleAutoCalc}
            style={{ ...btn(false, { background: "rgba(39,174,96,0.2)", color: "#5AE88A", border: "1px solid rgba(39,174,96,0.35)", whiteSpace: "nowrap" }) }}>
            ✓ Calculer les quantités auto
          </button>
        )}
        <button onClick={onClose}
          style={{ ...btn(false, { background: "rgba(255,80,80,0.15)", color: "#FF9090" }) }}>
          ✕ Fermer
        </button>
      </div>

      {/* ── CANVAS + PANEL ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* ── Canvas ── */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: 6, position: "relative" }}>
          <canvas
            ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", cursor: mode !== "idle" ? "crosshair" : "default" }}
            onClick={handleClick}
            onDoubleClick={handleDblClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setMouse(null)}
          />
          {/* Instructions overlay */}
          {mode !== "idle" && (
            <div style={{ position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.82)", color: WHITE, padding: "7px 20px", borderRadius: 8, fontSize: 12, border: `1px solid ${GOLD}`, pointerEvents: "none", whiteSpace: "nowrap" }}>
              {mode === "calibrate" && calPts.length === 0 && "Cliquez sur le 1er point d'une cote connue"}
              {mode === "calibrate" && calPts.length === 1 && "Cliquez sur le 2ème point — puis saisissez la distance réelle →"}
              {mode === "polygon"   && drawPts.length === 0 && "Cliquez pour tracer le contour — double-clic pour fermer la surface"}
              {mode === "polygon"   && drawPts.length > 0   && `${drawPts.length} point(s) — double-clic pour fermer la surface`}
              {mode === "line"      && drawPts.length === 0 && "Cliquez sur le point de départ"}
              {mode === "line"      && drawPts.length === 1 && "Cliquez sur le point d'arrivée"}
            </div>
          )}
        </div>

        {/* ── Right panel ── */}
        <div style={{ width: 272, background: "#0F0D0B", borderLeft: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", overflowY: "auto" }}>

          {/* ── Building params ── */}
          {showParams && (
            <div style={{ padding: 14, borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
              <div style={{ color: GOLD, fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", marginBottom: 10 }}>PARAMÈTRES BÂTIMENT</div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ color: "#7A6E65", fontSize: 11, display: "block", marginBottom: 3 }}>Type de construction</label>
                <select value={buildingParams.typeConstruction}
                  onChange={e => setBuildingParams(p => ({ ...p, typeConstruction: e.target.value }))}
                  style={{ ...inp, padding: "5px 8px" }}>
                  {["résidentiel", "villa", "appartement", "commercial", "bureaux", "hôtel", "mixte", "autre"].map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ color: "#7A6E65", fontSize: 11, display: "block", marginBottom: 3 }}>Localisation / Zone</label>
                <input value={buildingParams.zone}
                  onChange={e => setBuildingParams(p => ({ ...p, zone: e.target.value }))}
                  placeholder="ex: Casablanca, zone côtière…" style={inp} />
              </div>

              {/* Per-level params */}
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 10 }}>
                <div style={{ color: "#7A6E65", fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                  NIVEAU : {activeLvl?.name}
                </div>

                <div style={{ marginBottom: 7 }}>
                  <label style={{ color: "#5A5048", fontSize: 11, display: "block", marginBottom: 2 }}>Nom du niveau</label>
                  <input value={activeLvl?.name || ""}
                    onChange={e => updateLevel(activeLvlIdx, { name: e.target.value })} style={inp} />
                </div>

                <div style={{ marginBottom: 7 }}>
                  <label style={{ color: "#5A5048", fontSize: 11, display: "block", marginBottom: 2 }}>Hauteur sous plafond (m)</label>
                  <input type="number" step="0.05" min="2" max="8"
                    value={activeLvl?.hsp || 2.8}
                    onChange={e => updateLevel(activeLvlIdx, { hsp: e.target.value })} style={inp} />
                </div>

                <div style={{ color: "#4A4038", fontSize: 10, marginTop: 8, marginBottom: 5, fontStyle: "italic" }}>
                  Données non visibles sur le plan :
                </div>

                <div style={{ marginBottom: 7 }}>
                  <label style={{ color: "#5A5048", fontSize: 11, display: "block", marginBottom: 2 }}>Surface plancher m² (si non mesurée)</label>
                  <input type="number" step="0.5" min="0"
                    value={activeLvl?.manualSurface || ""}
                    onChange={e => updateLevel(activeLvlIdx, { manualSurface: e.target.value })}
                    placeholder="laisser vide si mesurée sur le plan" style={inp} />
                </div>

                <div style={{ marginBottom: 4 }}>
                  <label style={{ color: "#5A5048", fontSize: 11, display: "block", marginBottom: 2 }}>Périmètre ml (si non mesuré)</label>
                  <input type="number" step="0.5" min="0"
                    value={activeLvl?.manualPerimetre || ""}
                    onChange={e => updateLevel(activeLvlIdx, { manualPerimetre: e.target.value })}
                    placeholder="laisser vide si mesuré sur le plan" style={inp} />
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <label style={{ color: "#7A6E65", fontSize: 11, display: "block", marginBottom: 3 }}>Notes / Spécifications</label>
                <textarea value={buildingParams.notes}
                  onChange={e => setBuildingParams(p => ({ ...p, notes: e.target.value }))}
                  rows={2} placeholder="Isolation, finitions, contraintes…"
                  style={{ ...inp, resize: "vertical", lineHeight: 1.5 }} />
              </div>
            </div>
          )}

          <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>

            {/* Calibration dialog */}
            {mode === "calibrate" && calPts.length === 2 && (
              <div style={{ background: "rgba(231,76,60,0.1)", border: "1px solid rgba(231,76,60,0.3)", borderRadius: 8, padding: 12 }}>
                <div style={{ color: CALIB_COLOR, fontWeight: 700, fontSize: 11, marginBottom: 6 }}>ÉTALONNER L'ÉCHELLE</div>
                <div style={{ color: WHITE, fontSize: 12, marginBottom: 6 }}>Distance réelle entre les 2 points :</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="number" step="0.01" min="0.01" value={calDist}
                    onChange={e => setCalDist(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && confirmCalibration()}
                    placeholder="ex: 0.90" autoFocus style={{ ...inp, flex: 1 }} />
                  <span style={{ color: "#7A6E65", fontSize: 12 }}>m</span>
                </div>
                {calErr && <div style={{ color: CALIB_COLOR, fontSize: 11, marginTop: 4 }}>{calErr}</div>}
                <button onClick={confirmCalibration}
                  style={{ marginTop: 8, width: "100%", background: CALIB_COLOR, color: WHITE, border: "none", borderRadius: 6, padding: "8px 0", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                  ✓ Valider l'échelle
                </button>
              </div>
            )}

            {/* Scale status */}
            {scale ? (
              <div style={{ background: "rgba(39,174,96,0.1)", border: "1px solid rgba(39,174,96,0.3)", borderRadius: 7, padding: "7px 10px", fontSize: 12, color: "#5AE88A" }}>
                ✓ Échelle : 1 m = {Math.round(scale)} px
              </div>
            ) : planType !== "none" ? (
              <div style={{ background: "rgba(231,76,60,0.07)", border: "1px dashed rgba(231,76,60,0.25)", borderRadius: 7, padding: "7px 10px", fontSize: 11, color: "#7A6E65" }}>
                ① Calibrez l'échelle avant de mesurer
              </div>
            ) : null}

            {/* Pending measure */}
            {pending && (
              <div style={{ background: "rgba(201,169,110,0.12)", border: `1px solid ${GOLD}`, borderRadius: 10, padding: 12 }}>
                <div style={{ color: GOLD, fontWeight: 700, fontSize: 11, marginBottom: 4 }}>MESURE PRÊTE</div>
                <div style={{ color: WHITE, fontSize: 24, fontWeight: 700 }}>{pending.label}</div>
                <div style={{ color: "#7A6E65", fontSize: 11, marginBottom: 10 }}>Injectez dans le tableau ou auto-calculez</div>
                <button onClick={() => { onMeasure({ value: pending.value, unite: pending.unite, label: pending.label }); setPending(null); }}
                  style={{ width: "100%", background: GOLD, color: INK, border: "none", borderRadius: 7, padding: "9px 0", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                  ➜ Injecter dans le tableau
                </button>
                <button onClick={() => setPending(null)}
                  style={{ width: "100%", background: "transparent", color: "#5A4E46", border: "none", marginTop: 5, cursor: "pointer", fontSize: 11 }}>
                  Ignorer
                </button>
              </div>
            )}

            {/* Level summary */}
            {(lvlSurface > 0 || lvlPerimetre > 0 || parseFloat(activeLvl?.manualSurface) > 0) && (
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 10 }}>
                <div style={{ color: "#7A6E65", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>
                  MESURES — {activeLvl?.name}
                </div>
                {lvlSurface > 0 && (
                  <div style={{ color: WHITE, fontSize: 13, marginBottom: 2 }}>
                    ⬡ Surface : <strong>{lvlSurface.toFixed(2)} m²</strong>
                  </div>
                )}
                {parseFloat(activeLvl?.manualSurface) > 0 && lvlSurface === 0 && (
                  <div style={{ color: "#C9A96E", fontSize: 12, marginBottom: 2 }}>
                    ⬡ Surface manuelle : <strong>{activeLvl.manualSurface} m²</strong>
                  </div>
                )}
                {lvlPerimetre > 0 && (
                  <div style={{ color: WHITE, fontSize: 13, marginBottom: 2 }}>
                    ↔ Périmètre : <strong>{lvlPerimetre.toFixed(2)} ml</strong>
                  </div>
                )}
                <div style={{ color: "#5A5048", fontSize: 11, marginTop: 2 }}>
                  HSP : {activeLvl?.hsp || 2.8} m
                </div>
                {levels.length > 1 && totalSurface > 0 && (
                  <div style={{ color: GOLD, fontSize: 11, marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 5 }}>
                    Total {levels.length} niveaux : {totalSurface.toFixed(2)} m²
                  </div>
                )}
              </div>
            )}

            {/* Measurements list */}
            {done.length > 0 && (
              <div>
                <div style={{ color: "#5A5048", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>
                  DÉTAIL DES MESURES
                </div>
                {done.map((m, i) => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "5px 9px", marginBottom: 3 }}>
                    <span style={{ color: m.type === "polygon" ? POLY_COLOR : LINE_COLOR, fontSize: 12 }}>
                      {m.type === "polygon" ? "⬡" : "↔"} #{i + 1}
                    </span>
                    <span style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>
                      {m.value.toFixed(2)} {m.type === "polygon" ? "m²" : "ml"}
                    </span>
                    <button onClick={() => onMeasure({ value: m.value, unite: m.type === "polygon" ? "m²" : "ml", label: `${m.value.toFixed(2)} ${m.type === "polygon" ? "m²" : "ml"}` })}
                      title="Injecter dans le tableau" style={{ background: "none", border: "none", color: GOLD, cursor: "pointer", fontSize: 14 }}>↗</button>
                    <button onClick={() => updateLevel(activeLvlIdx, { done: done.filter(x => x.id !== m.id) })}
                      style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 12 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Help */}
            <div style={{ marginTop: "auto", padding: 10, background: "rgba(255,255,255,0.02)", borderRadius: 8, fontSize: 10, color: "#4A4038", lineHeight: 1.9 }}>
              <strong style={{ color: "#6A5D52" }}>Guide :</strong><br />
              <span style={{ color: CALIB_COLOR }}>①</span> Étalonner l'échelle (cliquer 2 points connus)<br />
              <span style={{ color: POLY_COLOR }}>⬡</span> Surface : cliquer les angles, double-clic pour fermer<br />
              <span style={{ color: LINE_COLOR }}>↔</span> Linéaire : clic départ → clic arrivée<br />
              <span style={{ color: GOLD }}>+</span> Ajoutez des onglets pour les plans multi-niveaux<br />
              <span style={{ color: "#5AE88A" }}>✓</span> Calculer auto : remplit le tableau d'estimatif
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
